// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { Tooltip } from '@mui/material';
import { Autocomplete as MuiAutocomplete } from '@mui/material';
import { AutocompleteProps as MuiAutocompleteProps } from '@mui/material';
import { Field } from 'mobx-react-form';
import { observer } from 'mobx-react';
import { observable, computed, makeObservable, action } from 'mobx';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import { alpha } from '@mui/material';
import TextField from '@shared/components/TextField';
import Flex from '@shared/components/Flex';
import Ellipsis from '@shared/components/Ellipsis';
import { getTestAttr } from '@shared/utils/common';
import { getScrollAttr } from '@shared/utils/form';
// import { Id } from '@shared/types/common';
import { getFieldOption, setFieldOption } from './Autocomplete.utils';

export interface Option {
  // id: Id;
  id: any;  // kludge to work around compile error.  Should be 'Id'
  name: string;
}

// Reduce an array of {id, name, ...} to array of {id, name}
export const normalize = (
  data: Array<{ [key: string]: any }>,
  properties: { id: string; name: string } = { id: 'id', name: 'name' }
) => {
  if(data !== undefined){
    return data.map((item): Option => ({ id: item[properties.id], name: item[properties.name] }));
  }
  return []
};

// Not quite sure what's going on here.  Following is my best guesses.
// Pick 'classes' from MuiAutoCompleteProps, then combined (&) with stuff from 'styles'.  
// 'classes' are from 'StandardProps' extended by MuiAutoCompleteProps.
type Classes = Pick<MuiAutocompleteProps<Option, boolean | undefined, boolean | undefined, boolean | undefined>, 'classes'>;

// this interface extends 'Classes' then adds back the MuiAutoCompleteProps but without its 'classes' as optionals.
// ie MuiAutoCompleteProps with original props optional, plus original 'classes' plus those from 'styles'.
export interface AutocompleteProps extends Classes,
    Omit<Partial<MuiAutocompleteProps<Option, boolean | undefined, boolean | undefined, boolean | undefined>>, 'classes'> {
  className?: string;
  options: Array<Option>;
  testAttributeArgs: {
    module: string;
    elementDescription: string;
  };
  multiple: boolean;
  name?: string;
  value?: Array<Option['id']> | Option['id'];
  valueName?: Array<Option['name']> | Option['name'];
  field?: Field;
  placeholder?: string;
  loading?: boolean;
  showSelectedValues?: boolean;
  showInputValue?: boolean;
  debounceTimeout?: number; // ms
  withCreateNewButton?: boolean;
  createButtonText?: string;
  selectedTooltipText?: string;
  selectedOptions?: Array<Option['id']>;
  withAsyncSearch?: boolean;
  error?: boolean;
  errorText?: React.ReactNode;
  clearOptions: () => any;
  onValueChange?: (ids: Array<Option['id']> | Option['id'], value?: Array<Option> | Option) => any;
  onInputChange?: (e: any) => any;
  onCreateClick?: () => any;
}

// TODO improve logic for multiple value

@observer
class Autocomplete extends React.Component<AutocompleteProps> {
  static defaultProps = {
    showSelectedValues: false,
    showInputValue: false,
    debounceTimeout: 500,
    popupIcon: null,
    withAsyncSearch: true,
    loadingText: 'Loading...'
  };
  @observable _inputValue = '';

  constructor(props: AutocompleteProps) {
    super(props);

    const { valueName, field } = props;
    const autocompleteValueName = field ? getFieldOption(field)?.name : valueName as Option['name'];

    this.setInputValue(autocompleteValueName);
    makeObservable(this);
  }

  private handleValueChange = (e, value: Array<Option> | Option) => {
    const { field, onValueChange, showInputValue } = this.props;
    let normalizedValue;
    let normalizedValueName;

    if (Array.isArray(value)) {
      normalizedValue = value.map(({ id }) => id);
      normalizedValueName = value.map(({ name }) => name);
    } else {
      normalizedValue = value?.id;
      normalizedValueName = value?.name;
    }

    if (onValueChange && value) {
      onValueChange(normalizedValue, value);
    }

    if (field) {
      field.container().update({ [field.name]: normalizedValue });
      this.validateField();
      setFieldOption(field, value as Option);
    }

    this.setInputValue(showInputValue ? normalizedValueName : undefined);
  }

  @action setInputValue = (label = '') => {
    const { field } = this.props;

    if (field) {
      setFieldOption(field, { id: field.value, name: label });
    } else {
      this._inputValue = label;
    }
  }

  get inputValue() {
    const { field } = this.props;

    if (field) {
      return getFieldOption(field)?.name || '';
    }

    return this._inputValue;
  }

  componentDidMount() {
    const { debounceTimeout, withAsyncSearch } = this.props;

    if (withAsyncSearch) {
      this.debouncedInputChange = debounce(this.debouncedInputChange, debounceTimeout);
    }
  }

  componentWillUnmount() {
    const { clearOptions } = this.props;

    clearOptions();
  }

  private debouncedInputChange = (value: string) => {
    
    const { onInputChange } = this.props;
  
    //if (onInputChange && value.length) { // NM: remove value.lenght to handle last backspace.
    if (onInputChange) {
      onInputChange(value);
    }

    const { field, multiple } = this.props;

    if (!value && field) {
      const fieldEmptyValue = multiple ? [] : '';

      field.set('value', fieldEmptyValue);
    }
  };

  private onInputChange = (e) => {
    e.persist();

    const { clearOptions } = this.props;
    const { value } = e.target;

    this.setInputValue(value);
    this.debouncedInputChange(value);
    clearOptions();
  }

  private handleInputBlur = () => {
    this.validateField();
  }

  private validateField = () => {
    const { field } = this.props;

    if (!field) {
      return;
    }

    field.container().validate(field.name);

    if (field.hasError) {
      field.showErrors();
    }
  }

  private handleCreate = (e) => {
    e.preventDefault();

    this.setInputValue('');

    const { onCreateClick } = this.props;

    if (onCreateClick) {
      onCreateClick();
    }
  }

  private renderPaper = ({ children }) => {
    const { withCreateNewButton, createButtonText, loading, loadingText } = this.props;
    const classes = withStyles.getClasses(this.props);

    if (loading) {
      return <div className={classes.loading}>{loadingText}</div>;
    }

    const createBtn = (
      <span
        className={classes.createBtn}
        onMouseDown={this.handleCreate}
      >
        {createButtonText}
      </span>
    );

    if (!this.inputValue) {
      return withCreateNewButton
        ? (
          <Flex
            autoWidth={false}
            alignItems="center"
            justifyContent="flex-end"
            className={classes.inputValue}
          >
            {createBtn}
          </Flex>
        )
        : null;
    }

    if (!withCreateNewButton) {
      return children;
    }

    return (
      <Flex className={classes.dropdownRoot} direction="column" alignItems="left">
        <Flex
          autoWidth={false}
          alignItems="center"
          justifyContent="space-between"
          className={classes.inputValue}
        >
          <span>{this.inputValue}</span>
          {withCreateNewButton && createBtn}
        </Flex>
        <div className={classes.searchResultsHeading}>Search results</div>
        {children}
      </Flex>
    );
  }

  // private renderOption = ({ id, name }: Option) => { // material-ui v4
  private renderOption = (props, option, state) => {    // mui v5
    // console.log('*** renderOptions options', props, option, state);
    const { selectedOptions = [], selectedTooltipText = '', testAttributeArgs } = this.props;
    const classes = withStyles.getClasses(this.props);
    const selected = selectedOptions.includes(option.id);

    return (
      <Tooltip title={selected ? selectedTooltipText : ''} placement="right">
        <div
          className={classes.tooltipWrapper}
          {...getTestAttr(testAttributeArgs.module, `${option.name}`, 'autocomplete-option')}
          {...props}
        >
          <Ellipsis
            width="100%"
            text={option.name}
            classes={{ root: cx(classes.option, { [classes.hiddenSelectedOption]: selected }) }}
          />
        </div>
      </ Tooltip>
    );
  }

  private filterOptions = (optionsList: Array<Option>, state) => {
    const { withAsyncSearch } = this.props;

    return withAsyncSearch
      ? optionsList
      : optionsList.filter((option) => option.name.toLowerCase().includes(state.inputValue.toLowerCase()));
  }

  private getOptionLabel = ({ name }: Option) => name || '';

  private get dropdownVisible() {
    const { loading } = this.props;

    return this.inputValue || loading;
  }

  render() {
    const {
      field,
      placeholder,
      loading,
      options,
      selectedOptions,
      testAttributeArgs,
      value,
      valueName,
      showSelectedValues,
      showInputValue,
      clearOptions,
      onValueChange,
      debounceTimeout,
      withAsyncSearch,
      withCreateNewButton,
      createButtonText,
      selectedTooltipText,
      onCreateClick,
      onInputChange,
      error,
      errorText,
      name,
      ...otherProps
    } = this.props;
    const classes = withStyles.getClasses(this.props);

    // HACK: Need to be only defined in `render` method to make react see field.error changes
    const hasError = Boolean(error || field?.error);

    const selectedValueId: number = field ? field.value : value;
    const selectedValueName = field ? getFieldOption(field)?.name : valueName;

    // TODO fix any type
    const selectedValue: any = {
      id: selectedValueId,
      name: selectedValueName
    };

    return (
      <MuiAutocomplete
        {...getTestAttr(testAttributeArgs.module, `${testAttributeArgs.elementDescription}`, 'autocomplete-field')}
        clearOnBlur
        loading={loading}
        options={options}
        value={showSelectedValues ? selectedValue : undefined}
        inputValue={this.inputValue}
        onChange={this.handleValueChange}
        getOptionLabel={this.getOptionLabel}
        filterOptions={this.filterOptions}
        // getOptionSelected={(option) => Boolean(selectedOptions?.includes(option.id))}    // Note: change back to this if there are problems with the search field.
        // getOptionSelected={(option, value) => option.name === value.name || Boolean(selectedOptions?.includes(option.id))}
        isOptionEqualToValue={(option, value) => option.name === value.name || Boolean(selectedOptions?.includes(option.id))}
        // getOptionDisabled={({ id }) => this.selectedOptions.includes(id)}
        classes={{
          root: classes.root,
          inputRoot: classes.inputRoot,
          input: classes.input,
          popper: this.dropdownVisible ? classes.dropdownRoot : classes.dropdownRootHidden,
          paper: classes.dropdownRootInner,
          noOptions: classes.noOptions,
          listbox: classes.optionsList,
          option: classes.option,
          loading: classes.loading,
          endAdornment: classes.endAdornment,
        }}
        PaperComponent={this.renderPaper}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              inputProps={{
                ...getScrollAttr(field?.name || name),
                ...params.inputProps,
              }}
              name={field?.name || name}
              helperText={field?.error || errorText}
              error={hasError}
              fullWidth
              margin="none"
              placeholder={placeholder}
              testAttributeArgs={testAttributeArgs}
              onChange={this.onInputChange}
              onBlur={this.handleInputBlur}
            />
          );
        }}
        // Temporarily disable this option. When it's on, the user cannot select any option
        // renderOption={this.renderOption}
        {...otherProps}
      />
    );
  }
}

const AutocompleteStyled = withStyles(
  Autocomplete, 
  (theme, props) => ({
    "root": {
      minWidth: 200,
    },
    "endAdornment": {
      display: 'none'
    },
    "dropdownRoot": {
      marginTop: theme.spacing(1),
      border: `1px solid ${theme.palette.colors.greyLight3}`,
      borderRadius: '4px',
      boxShadow: `0px 0px 8px ${alpha(theme.palette.colors.black, .08)}`,
      backgroundColor: theme.palette.colors.white,
    },
    "dropdownRootHidden": {
      opacity: 0,
    },
    "dropdownRootInner": {
      marginBottom: 0,
    },
    "noOptions": {
      padding: theme.spacing(3, 4),
    },
    "optionsList": {
      padding: theme.spacing(2),
    },
    "option": {
      fontSize: 15,
      borderRadius: '4px',
      padding: theme.spacing(1),

      '&[aria-selected=true]': {
        backgroundColor: 'transparent'
      },

      '&[aria-disabled=true]': {
        // pointerEvents: 'fill',
        cursor: 'default',
      }
    },
    "hiddenSelectedOption": {
      backgroundColor: 'transparent',
      opacity: 0.5,
    },
    "createItemSection": {
      padding: theme.spacing(4, 4, 2),
    },
    "searchResultsHeading": {
      fontSize: 13,
      color: theme.palette.colors.greyLight3,
      padding: theme.spacing(0, 4),
    },
    "createBtn": {
      fontSize: 12,
      fontWeight: 500,
      color: theme.palette.colors.primaryAccent,
      cursor: 'pointer',
    },
    "tooltipWrapper": {
      width: '100%',
    },
    "inputRoot": {
      padding: '0 !important',
    },
    "input": {
      padding: '0 !important',
    },
    "inputValue": {
      padding: theme.spacing(4),
    },
    "loading": {
      color: theme.palette.colors.greyLight3,
      padding: theme.spacing(3, 4),
    }
  })
);

export default AutocompleteStyled;